import React, { useEffect, useRef, useState } from 'react'
import { ApolloError, ApolloQueryResult } from '@apollo/client'
import { IconPosition } from '@at/design-system-elements'
import { atdsTheme, ThemeSelectors } from '@at/design-system-foundation'
import { useRetailerStockSearch } from '../../utils/retailer-stock'
// eslint-disable-next-line import/no-webpack-loader-syntax
import InfiniteScroll from 'react-infinite-scroll-component'
import {
    RetailerStockWrapper,
    StyledBackToTop,
    StyledLoadPreviousResultsButton,
    StyledPreviousResultsButtonContainer,
    StyledSearch,
} from './search-listings.styles'

import { useSearchPage } from '../../forms/hocs/search-page.context'
import { RetailerTitle } from './retailer-title'
import { animateScroll as scroll } from 'react-scroll'
import { containsFilterError, FilterErrorType, isFilterError, isPostcodeFilterError } from '../../utils/query-utilities'
import { IconArrowUp, IconChevronUp } from '@at/design-system-icons'
import { useSearchViewState } from '../../contexts/search-view-state-context'
import { useForm } from '../../forms/hocs/form.context'
import { usePagesLoadedContext } from '../../contexts/pages-loaded-context'
import { ComponentViewDispatcher } from '../../pages/search-results/component-view-dispatcher'
import { SearchCountAndAdsDisclaimerContainer } from '../../components/search-count-and-ads-disclaimer/search-count-and-ads-disclaimer'
import {
    SearchResultsListingsGridQuery_Query,
    SearchResultsListingsGridQuery_searchResults_SearchResults_intercept_InterceptCTA,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings,
} from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { StyledSearchUlSkeleton } from './search-listings.skeleton'
import { SearchUl } from './search-ul'
import {
    EndPagination,
    SearchListingsError,
    SearchListingsExpiredResults,
    SearchListingsMaximumResults,
} from './search-listings-messages'
import { LeasingSummaryText } from '../../components/leasing-summary-text/leasing-summary-text'
import { ModalLoginContextProvider } from '../../contexts/modal-login-context'

const ADVERT_NUMBER_TO_SHOW_GO_TOP_BUTTON = 4
const READY = 7

interface SearchListingsProps {
    listings: ReadonlyArray<SearchResultsListingsGridQuery_searchResults_SearchResults_listings>
    error?: ApolloError
    intercept?: SearchResultsListingsGridQuery_searchResults_SearchResults_intercept_InterceptCTA | null | undefined
    pageCount?: number
    queryPage?: number
    savedAdverts: string[]
    totalResults: number
    searchId: string
    loading: boolean
    networkStatus: number
    fetchMore: (
        page: number,
        previous?: boolean
    ) => Promise<ApolloQueryResult<SearchResultsListingsGridQuery_Query> | undefined>
    leasingSummary?: string | null
}

interface MobileSearchListingsProps extends SearchListingsProps {
    fetchMore: (
        page: number,
        previous?: boolean
    ) => Promise<ApolloQueryResult<SearchResultsListingsGridQuery_Query> | undefined>
}

interface InfiniteScrollProps extends Omit<MobileSearchListingsProps, 'fetchMore'> {
    fetchNext: () => Promise<ApolloQueryResult<SearchResultsListingsGridQuery_Query> | undefined>
}

const InfiniteScrollListings = ({
    pageCount = 1,
    queryPage = 1,
    listings = [],
    fetchNext,
    savedAdverts = [],
    totalResults,
    searchId,
    intercept,
}: InfiniteScrollProps) => {
    const { toggleFooter } = useSearchViewState()
    const [displayToTopButton, setDisplayToTopButton] = useState(false)
    const searchUlContainerRef = useRef<HTMLUListElement | null>(null)
    const page = queryPage
    const numberOfPages = pageCount // pageCount is always 1 from facets query

    useEffect(() => {
        const lastPage = page >= numberOfPages
        toggleFooter(lastPage)
    }, [toggleFooter, page, numberOfPages])

    /** Display or hide "Go to Top" button if infinite scroll is active */
    const onScrollResults = () => {
        const searchResultsChildren = searchUlContainerRef.current?.children[ADVERT_NUMBER_TO_SHOW_GO_TOP_BUTTON]
        const rect = searchResultsChildren ? searchResultsChildren.getBoundingClientRect() : false

        setDisplayToTopButton(
            searchUlContainerRef && rect
                ? rect!.bottom <= (window.innerHeight || document.documentElement.clientHeight)
                : false
        )
    }

    const [pageLimitReached, setPageLimitReached] = useState(false)

    return (
        <StyledSearch data-testid="desktop-search">
            <InfiniteScroll
                dataLength={listings.length}
                next={async () => {
                    try {
                        const result = await fetchNext()
                        if (result && result.errors && containsFilterError(result.errors, FilterErrorType.PAGE)) {
                            setPageLimitReached(true)
                        }
                    } catch (error) {
                        isFilterError(error as ApolloError, FilterErrorType.PAGE) && setPageLimitReached(true)
                    }
                }}
                hasMore={page < numberOfPages}
                loader={!pageLimitReached && <StyledSearchUlSkeleton />}
                endMessage={totalResults === 0 ? null : <SearchListingsMaximumResults />} // Message to display when the user when they have viewed all possible results
                onScroll={onScrollResults}
            >
                <SearchUl
                    searchUlContainerRef={searchUlContainerRef}
                    listings={listings}
                    savedAdverts={savedAdverts}
                    page={page}
                    searchId={searchId}
                    intercept={intercept}
                />
            </InfiniteScroll>
            {
                pageLimitReached && <SearchListingsMaximumResults /> // scenario when theoretically there is more data, but VLS returns the page limit reached error which restricts the user from seeing more - i.e. stop scraping activity
            }
            {displayToTopButton && (
                <StyledBackToTop onClick={() => scroll.scrollToTop()} data-gui="go-to-top-button">
                    <IconChevronUp
                        title="Back to top"
                        customSize={{ width: 12, height: 12 }}
                        fill={ThemeSelectors.getWhite(atdsTheme)}
                    />
                </StyledBackToTop>
            )}
        </StyledSearch>
    )
}

export const SearchListings = ({
    error,
    listings = [],
    pageCount = 1,
    queryPage = 1,
    savedAdverts = [],
    loading,
    networkStatus,
    fetchMore,
    searchId,
    leasingSummary,
    intercept = null,
}: SearchListingsProps) => {
    const { queryParams } = useSearchPage()
    const [isRetailerStockSearch, retailerId] = useRetailerStockSearch()
    const { totalResults, sortByDisplayTooltip } = useForm()

    const isReturnedFromExpiredAd = queryParams.get('expired-ad')
    const serverErrorsPage = isFilterError(error, FilterErrorType.PAGE)
    const hasPostcodeError = isPostcodeFilterError(error)
    const { nextPage, previousPage } = usePagesLoadedContext()

    useEffect(() => {
        if (!loading && networkStatus === READY) {
            ComponentViewDispatcher.reset()
        }
    }, [loading, networkStatus])

    const fetchNext = async () => {
        return await fetchMore(nextPage)
    }

    const fetchPrevious = async () => {
        await fetchMore(previousPage, true)
    }

    return (
        <>
            {!serverErrorsPage && !hasPostcodeError && error && <SearchListingsError />}
            {serverErrorsPage && <EndPagination />}
            {isReturnedFromExpiredAd && <SearchListingsExpiredResults />}
            {!error && (
                <>
                    {isRetailerStockSearch && (
                        <RetailerStockWrapper>
                            <RetailerTitle retailerId={retailerId} />
                            <SearchCountAndAdsDisclaimerContainer
                                totalResults={totalResults}
                                sortDescription={sortByDisplayTooltip}
                                position={'left'}
                                isRetailerStock={isRetailerStockSearch}
                            />
                        </RetailerStockWrapper>
                    )}
                    <LeasingSummaryText leasingSummary={leasingSummary} />
                    {!!previousPage ? (
                        <StyledPreviousResultsButtonContainer>
                            <StyledLoadPreviousResultsButton
                                text="Load previous results"
                                onInteraction={fetchPrevious}
                                displayType="tertiary"
                                iconSrc={<IconArrowUp title="up arrow" />}
                                iconPosition={IconPosition.LEFT}
                                data-testid="load-previous-button"
                            />
                        </StyledPreviousResultsButtonContainer>
                    ) : null}
                    <ModalLoginContextProvider trigger="SAVE_ADVERT">
                        <InfiniteScrollListings
                            pageCount={pageCount}
                            queryPage={queryPage}
                            listings={listings}
                            fetchNext={fetchNext}
                            savedAdverts={savedAdverts}
                            totalResults={totalResults}
                            searchId={searchId}
                            loading={loading}
                            networkStatus={networkStatus}
                            intercept={intercept}
                        />
                    </ModalLoginContextProvider>
                </>
            )}
        </>
    )
}
